import {
  tablesDB, DATABASE_ID, Query, ID,
  DELIVERY_RATINGS_TABLE_ID as RATINGS_TABLE_ID,
  DRIVERS_TABLE_ID,
  REQUESTS_TABLE_ID,
} from '../lib/appwrite';

// ─── Ratings ──────────────────────────────────────────────────────────────────

export async function listRatings(filters = {}) {
  const queries = [Query.limit(500), Query.orderDesc('$createdAt')];
  if (filters.driverId) queries.push(Query.equal('driverId', filters.driverId));
  if (filters.isComplaint !== undefined) queries.push(Query.equal('isComplaint', filters.isComplaint));
  if (filters.complaintStatus) queries.push(Query.equal('complaintStatus', filters.complaintStatus));
  const res = await tablesDB.listRows(DATABASE_ID, RATINGS_TABLE_ID, queries);
  return res.rows || [];
}

export async function getRating(ratingId) {
  return tablesDB.getRow(DATABASE_ID, RATINGS_TABLE_ID, ratingId);
}

export async function updateComplaintStatus(ratingId, status) {
  return tablesDB.updateRow(DATABASE_ID, RATINGS_TABLE_ID, ratingId, { complaintStatus: status });
}

export async function deleteRating(ratingId) {
  return tablesDB.deleteRow(DATABASE_ID, RATINGS_TABLE_ID, ratingId);
}

// ─── Driver Rating Aggregation ────────────────────────────────────────────────

export async function recalculateDriverRating(driverId) {
  const res = await tablesDB.listRows(DATABASE_ID, RATINGS_TABLE_ID, [
    Query.equal('driverId', driverId),
    Query.limit(1000),
  ]);
  const ratings = (res.rows || []).filter(r => r.rating != null && r.rating > 0);
  if (!ratings.length) return null;

  const avg = ratings.reduce((s, r) => s + (r.rating || 0), 0) / ratings.length;
  await tablesDB.updateRow(DATABASE_ID, DRIVERS_TABLE_ID, driverId, {
    averageRating: Math.round(avg * 10) / 10,
    totalRatings: ratings.length,
  });
  return { averageRating: avg, totalRatings: ratings.length };
}

// ─── Ratings with enriched request data ───────────────────────────────────────

export async function listRatingsWithDetails(filters = {}) {
  const ratings = await listRatings(filters);
  if (!ratings.length) return [];

  // Enrich with request data in parallel (best-effort)
  const requestIds = [...new Set(ratings.map(r => r.requestId).filter(Boolean))];
  const requestMap = {};
  await Promise.allSettled(
    requestIds.map(async id => {
      try {
        const req = await tablesDB.getRow(DATABASE_ID, REQUESTS_TABLE_ID, id);
        requestMap[id] = req;
      } catch {}
    })
  );

  return ratings.map(r => ({
    ...r,
    request: requestMap[r.requestId] || null,
  }));
}

// ─── Stats ────────────────────────────────────────────────────────────────────

export function computeRatingStats(ratings) {
  const total = ratings.length;
  if (!total) return { total: 0, avgRating: 0, complaints: 0, pendingComplaints: 0, distribution: {} };

  const withRating = ratings.filter(r => r.rating > 0);
  const avgRating = withRating.length
    ? withRating.reduce((s, r) => s + r.rating, 0) / withRating.length
    : 0;

  const complaints = ratings.filter(r => r.isComplaint).length;
  const pendingComplaints = ratings.filter(r => r.isComplaint && r.complaintStatus === 'pending').length;

  const distribution = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
  withRating.forEach(r => { if (r.rating >= 1 && r.rating <= 5) distribution[r.rating]++; });

  return { total, avgRating: Math.round(avgRating * 10) / 10, complaints, pendingComplaints, distribution };
}
